import { useAppDispatch, useAppSelector } from "@/redux/features/hooks";
import { useLogoutMutation } from "@/redux/services/auth/auth.api";
import { Roles } from "@/redux/services/user/user.type";
import { performLogout } from "@/lib/logout";
import toast from "react-hot-toast";
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Home,
  ShoppingCart,
  Users,
  BookOpen,
  Settings,
  LogOut,
  Monitor,
  ChefHat,
  Banknote,
  PanelLeftClose,
  Maximize,
  Minimize,
  Package,
  BarChart3,
  Receipt,
  // ClipboardList,
} from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import {
  POS_PERMISSIONS,
  HUB_PERMISSIONS,
  ORDERS_VIEW_PERMISSIONS,
  isKitchenOnly,
  kitchenOnlyMayReach,
} from "@/lib/navigation";

interface NavRoute {
  label: string;
  icon: typeof LayoutDashboard;
  href: string;
  roles: Roles[];
  permissions?: string[];
}

const routes: NavRoute[] = [
  {
    label: "Tableau de bord",
    icon: LayoutDashboard,
    href: "dashboard",
    roles: [Roles.manager, Roles.owner],
    permissions: ["dashboard.view"],
  },
  {
    // The floor-staff launchpad: opens on orders.manage like the POS it
    // leads to (HUB_PERMISSIONS, same gate as the /hub page).
    label: "Accueil",
    icon: Home,
    href: "hub",
    roles: [Roles.manager, Roles.cashier, Roles.waiter],
    permissions: HUB_PERMISSIONS,
  },
  {
    // Opens on orders.manage (POS_PERMISSIONS), like the /pos route guard.
    // Kitchen-only users hold it too but are filtered out below.
    label: "POS",
    icon: Monitor,
    href: "pos",
    roles: [Roles.manager, Roles.owner, Roles.cashier, Roles.waiter],
    permissions: POS_PERMISSIONS,
  },
  {
    label: "Commandes",
    icon: ShoppingCart,
    href: "orders",
    roles: [
      Roles.manager,
      Roles.waiter,
      Roles.kitchen,
      Roles.cashier,
      Roles.owner,
    ],
    // Opens on orders.view, like the /orders route guard (SFRESTO-184).
    // Cashiers, waiters and kitchen hold it by default.
    permissions: ORDERS_VIEW_PERMISSIONS,
  },
  {
    // Operational — driven by functional role (kitchen), not a granular
    // permission.
    label: "Cuisine",
    icon: ChefHat,
    href: "kitchen",
    roles: [Roles.manager, Roles.kitchen],
  },
  {
    label: "Caisse",
    icon: Banknote,
    href: "register",
    roles: [Roles.manager, Roles.cashier],
    permissions: ["cash_register.view"],
  },
  {
    label: "Inventaire",
    icon: Package,
    href: "inventory",
    roles: [Roles.manager, Roles.owner],
    permissions: ["inventory.view", "suppliers.view"],
  },
  {
    label: "Dépenses",
    icon: Receipt,
    href: "expenses",
    roles: [Roles.manager, Roles.owner],
    permissions: ["expenses.view"],
  },
  {
    label: "Rapports",
    icon: BarChart3,
    href: "reports",
    roles: [Roles.manager, Roles.owner],
    permissions: ["reports.view"],
  },
  {
    label: "Clients",
    icon: Users,
    href: "customers",
    roles: [Roles.manager, Roles.owner, Roles.cashier],
    permissions: ["customers.view"],
  },
  {
    label: "Menus",
    icon: BookOpen,
    href: "menus",
    roles: [Roles.manager, Roles.owner],
    permissions: ["menu.view"],
  },
  // {
  //   label: "Journal d'activité",
  //   icon: ClipboardList,
  //   href: "activity-logs",
  //   roles: [Roles.manager, Roles.owner],
  // },
  {
    label: "Paramètres",
    icon: Settings,
    href: "settings",
    roles: [Roles.manager, Roles.owner],
    permissions: ["settings.view"],
  },
];

const Sidebar = ({
  isOpen,
  isCollapsed = false,
  onCollapse,
}: {
  isOpen: boolean;
  isCollapsed?: boolean;
  onCollapse?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentUser = useAppSelector((state) => state.user.currentUser);
  const [logout] = useLogoutMutation();
  const { canAny } = usePermissions();
  // Permission-gated routes are decided by permissions alone (full-access
  // users pass via canAny); the roles list only drives the role-based routes
  // without permissions (Cuisine). Otherwise a secondary manager's
  // ROLE_MANAGER would surface every entry their permissions deny.
  // Kitchen-only users only see what ProtectedPage lets them reach.
  const kitchenOnly = isKitchenOnly(currentUser?.roles);
  const filteredRoutes = routes.filter((route) => {
    if (kitchenOnly && !kitchenOnlyMayReach(`/${route.href}`, canAny)) {
      return false;
    }
    const roleMatch = currentUser?.roles?.some((role) =>
      route.roles.includes(role)
    );
    if (!route.permissions?.length) return roleMatch;
    return canAny(...route.permissions);
  });
  const confirm = useConfirm();

  const [isFullscreen, setIsFullscreen] = useState<boolean>(
    () => typeof document !== "undefined" && !!document.fullscreenElement
  );

  // True when the page is already running inside a true-fullscreen shell —
  // installed PWA in display-mode: fullscreen, Chrome launched with --kiosk,
  // or browser F11. In those cases the toggle has nothing to do at the
  // document level. In standalone / minimal-ui (Android bars still visible)
  // we keep the toggle so staff can try to hide them.
  const [isAppShellFullscreen, setIsAppShellFullscreen] = useState<boolean>(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(display-mode: fullscreen)").matches
  );

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(display-mode: fullscreen)");
    const update = () => setIsAppShellFullscreen(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch {
      toast.error("Le plein écran n'est pas disponible");
    }
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: "Voulez-vous vraiment vous déconnecter ?",
      confirmText: "Déconnexion",
      cancelText: "Annuler",
      icon: <LogOut className="h-6 w-6" />,
    });
    if (!ok) return;
    await performLogout(dispatch, () => logout().unwrap());
    navigate("/auth/login");
    toast.success("Déconnexion réussie !");
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 flex h-full w-[270px] flex-col bg-white border-r border-border overflow-hidden transition-all duration-300 ease-in-out xl:static xl:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full",
        isCollapsed && "xl:w-0 xl:border-r-0"
      )}
    >
      <div className="flex flex-col h-full overflow-y-auto scroll-hidden">
        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 pt-4">
          <ul className="flex flex-col gap-1">
            {filteredRoutes.map(({ label, icon: Icon, href }) => (
              <li key={href}>
                <NavLink
                  to={`/${href}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "text-primary bg-primary/5"
                        : "hover:text-primary"
                    )
                  }
                >
                  <Icon className="h-[18px] w-[18px] flex-shrink-0" />
                  <span>{label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Chrome controls + logout at the bottom */}
        <div className="px-4 py-4 border-t border-border">
          {!isAppShellFullscreen && (
            <button
              onClick={toggleFullscreen}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:text-primary transition-colors w-full"
              aria-label={isFullscreen ? "Quitter le plein écran" : "Passer en plein écran"}
            >
              {isFullscreen ? (
                <Minimize className="h-[18px] w-[18px] flex-shrink-0" />
              ) : (
                <Maximize className="h-[18px] w-[18px] flex-shrink-0" />
              )}
              <span>{isFullscreen ? "Quitter plein écran" : "Plein écran"}</span>
            </button>
          )}
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="hidden xl:flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:text-primary transition-colors w-full"
              aria-label="Masquer la barre latérale"
            >
              <PanelLeftClose className="h-[18px] w-[18px] flex-shrink-0" />
              <span>Masquer</span>
            </button>
          )}
          <div className="my-2 border-t border-border" />
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium hover:text-primary transition-colors w-full"
          >
            <LogOut className="h-[18px] w-[18px] flex-shrink-0" />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
